import { FC } from 'react';
import {
  DropdownMenu,
  Button,
  Icon,
  SocialMediaIcon,
  SocialMediaIconProps,
} from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { faImageSlash } from '@fortawesome/pro-regular-svg-icons/faImageSlash';
import { ApprovedPost } from '../../context/ApprovedPostsContext';
import PageTypeUtils from 'app/utils/pageType';
import clsx from 'clsx';

interface ApprovedPostDropdownItemProps {
  post: ApprovedPost;
  onRevertApproval: (post: ApprovedPost) => void;
  closeDropdown: () => void;
  isReverting: boolean;
}

const ApprovedPostDropdownItem: FC<ApprovedPostDropdownItemProps> = ({
  post,
  onRevertApproval,
  closeDropdown,
  isReverting,
}) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu.Item
      className="tw-mx-0 tw-flex tw-h-10 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-bg-grayscale-10 tw-px-4 tw-py-2 tw-transition-colors hover:tw-bg-grayscale-20"
      data-name={`approvals-approved-post-item-${post.id}`}
      data-cy={`approvals-approved-post-item-${post.id}`}
      onSelect={(e) => e.preventDefault()}
    >
      <div className="tw-relative tw-h-6 tw-w-6 tw-shrink-0 tw-overflow-hidden tw-rounded-sm">
        {post.thumbnail ? (
          <img
            src={post.thumbnail}
            alt={post.title}
            className="tw-h-full tw-w-full tw-object-cover"
          />
        ) : (
          <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-grayscale-20">
            <Icon
              icon={faImageSlash}
              className="tw-text-grayscale-50"
              size={12}
            />
          </div>
        )}
        <SocialMediaIcon
          size={12}
          type={
            PageTypeUtils.getName(post.pageType) as SocialMediaIconProps['type']
          }
          className="tw-absolute tw-bottom-0 tw-right-0"
        />
      </div>
      <span
        className={clsx('tw-flex-1 tw-truncate tw-text-sm tw-font-medium', {
          'tw-text-grayscale-180': post.title,
          'tw-italic tw-text-grayscale-50': !post.title,
        })}
      >
        {post.title ||
          t('approvalsPostWithoutText', {
            defaultValue: 'Post is without text',
          })}
      </span>
      <Button
        variant="ghost"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          closeDropdown();
          onRevertApproval(post);
        }}
        className="tw-text-sm"
        data-name={`approvals-approved-post-undo-${post.id}`}
        data-cy={`approvals-approved-post-undo-${post.id}`}
        disabled={isReverting}
        isLoading={isReverting}
      >
        {t('undo', { defaultValue: 'Undo' })}
      </Button>
    </DropdownMenu.Item>
  );
};

export default ApprovedPostDropdownItem;
